/**
 * <mrk-license-dialog>: global quick-license dialog. Opens on `mrk:license:open` ({ handle }), fetches the picker via
 * the Section Rendering API (section `mrk-license-dialog-content`) and closes after a successful add to cart, at which
 * point Horizon's cart drawer opens (it defers its auto-open until a source modal dialog closes).
 * Also owns the single site-wide click handler for [data-mrk-license-open] (rows, cards, upsell cards).
 * Motion: panel {opacity 0, y 8, scale .97} → {1, 0, 1}, ease [.32,.72,0,1] ~.22s, backdrop fade (CSS in the snippet).
 */
import { MRK_EVENTS, emit, on } from './mrk-events.js';

const SECTION_ID = 'mrk-license-dialog-content';
const ADDED_EVENT = 'mrk:license:added';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class MrkLicenseDialog extends HTMLElement {
  /** @type {HTMLElement | null} */
  #opener = null;
  /** @type {AbortController | null} */
  #request = null;
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /** @type {number | undefined} */
  #closeTimer;

  get dialog() {
    return /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
  }

  get body() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-mrk-dialog-body]'));
  }

  connectedCallback() {
    this.#unsubscribe = on(MRK_EVENTS.licenseOpen, (event) => this.open(event.detail || {}));
    const { dialog } = this;
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('click', this.#onClick);
    dialog.addEventListener('keydown', this.#onKeydown);
    dialog.addEventListener(ADDED_EVENT, this.#onAdded);
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    const { dialog } = this;
    dialog.removeEventListener('cancel', this.#onCancel);
    dialog.removeEventListener('click', this.#onClick);
    dialog.removeEventListener('keydown', this.#onKeydown);
    dialog.removeEventListener(ADDED_EVENT, this.#onAdded);
  }

  /** @param {{ handle?: string }} detail */
  async open({ handle } = {}) {
    if (!handle) return;
    const { dialog } = this;
    clearTimeout(this.#closeTimer);

    if (!dialog.open) {
      this.#opener = /** @type {HTMLElement | null} */ (document.activeElement);
      dialog.showModal();
      document.documentElement.classList.add('mrk-scroll-locked');
      requestAnimationFrame(() => dialog.classList.add('mrk-license-dialog--open'));
    }

    this.#setState(this.dataset.mrkLabelLoading || '', 'loading');
    dialog.querySelector('[data-mrk-dialog-close]')?.focus();

    this.#request?.abort();
    this.#request = new AbortController();
    const root = window.Theme?.routes?.root || '/';
    const url = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;

    try {
      const [response] = await Promise.all([
        fetch(url, { signal: this.#request.signal, credentials: 'same-origin' }),
        // Same versioned URL as the product page's <script>, so the module loads once and stays fresh after deploys.
        import(this.dataset.mrkProductModule || './mrk-product.js'),
      ]);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-mrk-dialog-content]');
      if (!content) throw new Error('Missing dialog content');

      const nodes = [...doc.querySelectorAll('link[rel="stylesheet"]'), content];
      this.body.replaceChildren(...nodes.map((node) => document.importNode(node, true)));
      dialog.removeAttribute('aria-label');
      dialog.setAttribute('aria-labelledby', 'mrk-license-dialog-title');

      const first = /** @type {HTMLElement | null} */ (
        this.body.querySelector('.mrk-tier__input:checked') || this.body.querySelector('[data-mrk-add]')
      );
      first?.focus({ preventScroll: true });
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      this.#setState(this.dataset.mrkLabelError || '', 'error');
    }
  }

  close() {
    const { dialog } = this;
    if (!dialog.open) return;
    this.#request?.abort();
    dialog.classList.remove('mrk-license-dialog--open');
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
    this.#closeTimer = window.setTimeout(() => {
      dialog.close();
      document.documentElement.classList.remove('mrk-scroll-locked');
      this.body.replaceChildren();
      dialog.removeAttribute('aria-labelledby');
      if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
      this.#opener = null;
    }, duration);
  }

  /** @param {string} text @param {'loading' | 'error'} kind */
  #setState(text, kind) {
    const state = document.createElement('p');
    state.className = `mrk-license-dialog__state mrk-license-dialog__state--${kind}`;
    state.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    state.textContent = text;
    this.body.replaceChildren(state);
  }

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === this.dialog) {
      // Click on the backdrop (outside the panel box).
      const rect = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) this.close();
      return;
    }
    if (target.closest('[data-mrk-dialog-close]') || target.closest('[data-mrk-offer-link]')) this.close();
  };

  /** Keeps Tab inside the panel (showModal already makes the page inert; this also stops escaping to browser UI). */
  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const focusables = /** @type {HTMLElement[]} */ ([...this.dialog.querySelectorAll(FOCUSABLE)]).filter(
      (element) => element.offsetParent !== null || element === document.activeElement
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #onAdded = () => this.close();
}

if (!customElements.get('mrk-license-dialog')) customElements.define('mrk-license-dialog', MrkLicenseDialog);

/**
 * Plain primary clicks on [data-mrk-license-open] open the dialog. On links, modified or non-primary clicks
 * (Ctrl/Cmd/Shift/Alt, middle click) keep the browser's default so the beat page can open in a new tab.
 * @param {MouseEvent} event
 */
function onLicenseOpenClick(event) {
  if (event.defaultPrevented) return;
  const trigger = event.target instanceof Element ? event.target.closest('[data-mrk-license-open]') : null;
  if (!(trigger instanceof HTMLElement) || !trigger.dataset.mrkHandle) return;
  const isLink = trigger instanceof HTMLAnchorElement && trigger.hasAttribute('href');
  if (isLink && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
  event.preventDefault();
  if (trigger.hasAttribute('data-mrk-in-cart')) {
    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else window.location.href = `${window.Shopify?.routes?.root ?? '/'}cart`;
    return;
  }
  emit(MRK_EVENTS.licenseOpen, {
    handle: trigger.dataset.mrkHandle,
    productId: Number(trigger.dataset.mrkProductId) || undefined,
  });
}

if (!window.mrkLicenseOpenBound) {
  window.mrkLicenseOpenBound = true;
  document.addEventListener('click', onLicenseOpenClick);
}
